<template>
    <UpsellAfterCancelModal ref="modalRef" v-if="modalKey" @confirm="onConfirm" @hidden="onDismiss" />
</template>

<script setup lang="ts">
// Самодостаточный хост апселл-модалки после отмены (FINCH-2324): сам запрашивает
// getUpsellModalInfo при входе и показывает модалку. Вынесен из TariffNotificationModals,
// чтобы не смешивать логику и не раздувать тарифный оркестратор.
// Сегмент апселла взаимоисключающий с сегментами тарифных модалок — двух модалок разом не будет.
import { onMounted, ref, nextTick } from 'vue';
import UpsellAfterCancelModal from './UpsellAfterCancelModal.vue';
import { UserModalsApi } from '@/api/UserModalsApi';

// После того как модалку уже показали/закрыли — не дёргаем эндпоинт повторно
// в рамках этой сессии вкладки (постоянный флаг). Приложение — MVC с Vue-островками,
// поэтому каждая навигация = новая загрузка страницы и ремонт компонента.
const SESSION_SKIP_KEY = 'upsellAfterCancel.skip';

// Ответ shouldShow=false кэшируем ненадолго (метка времени последней проверки):
// это отсекает запрос сегмента на каждой загрузке страницы, но состояние пользователя
// успеет переоткрыться по истечении TTL, если он попал в сегмент позже.
const SESSION_NOTHING_KEY = 'upsellAfterCancel.checkedAt';
const NOTHING_TTL_MS = 10 * 60 * 1000;

const modalRef = ref<InstanceType<typeof UpsellAfterCancelModal> | null>(null);
const modalKey = ref<string | null>(null);
let dismissed = false;

async function onDismiss() {
    if (!modalKey.value || dismissed) {
        return;
    }
    dismissed = true;
    sessionStorage.setItem(SESSION_SKIP_KEY, '1');
    try {
        await new UserModalsApi().dismissUpsellModal(modalKey.value);
    } catch (e) {
        console.error(e);
    }
}

async function onConfirm() {
    await onDismiss();
    window.location.href = '/PaidFeatures';
}

onMounted(async () => {
    if (sessionStorage.getItem(SESSION_SKIP_KEY)) {
        return;
    }
    const checkedAt = Number(sessionStorage.getItem(SESSION_NOTHING_KEY));
    if (checkedAt && Date.now() - checkedAt < NOTHING_TTL_MS) {
        return;
    }
    try {
        const info = await new UserModalsApi().getUpsellModalInfo();
        if (!info?.entity?.shouldShow || !info.entity.modalKey) {
            // Кэшируем «нечего показывать» только для настоящего ответа бэка.
            // При ошибке/редиректе на логин get() возвращает undefined (info == null) —
            // такой результат НЕ кэшируем, иначе неавторизованный ранний запрос
            // заблокирует показ на весь TTL (и обновлял бы метку каждой загрузкой).
            if (info?.isSuccess) {
                sessionStorage.setItem(SESSION_NOTHING_KEY, String(Date.now()));
            }
            return;
        }
        modalKey.value = info.entity.modalKey;
        await nextTick();
        modalRef.value?.show();
    } catch (e) {
        console.error(e);
    }
});
</script>
